import { useTranslation } from 'react-i18next';
import type { Referral } from '@/hooks/useReferrals';
import { DiagnosisField } from '@/components/patient/ReferralDiagnosisField';
import { Button } from '@/components/ui/Button';
import { DanishDateField } from '@/components/ui/DanishDateField';
import { TimeInput } from '@/components/ui/TimeInput';
import { diagnoseTextForReferral } from '@/lib/referral/xref01Validation';
import {
  EPIKRISE_CONTACT_MAX,
  EPIKRISE_DATE_ELEMENTS,
  EPIKRISE_MAIN_ADDITIONAL_MAX,
  EPIKRISE_RELATIVES_MAX,
  epikriseHasRadiologyExtras,
  epikriseRequiresMainDiagnosis,
  epikriseSignature,
  type EpikriseForm,
  type EpikriseIssue,
  type EpikriseKind,
} from '@/lib/referral/epikrise';
import { FORM_ERROR_TYPE, FORM_HINT_TYPE, FORM_LABEL_TYPE, TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { FIELD, HEADING, SUB } from '@/components/referrals/epikrisePanelShared';

/** Diagnoses (DIS0x-S 4.6/4.7, DIS05-S 4.9/4.10), dates, kontaktperson and pårørende. */
export function EpikriseClinicalFields({
  referral,
  form,
  update,
  attempted,
  issues,
  signer,
}: {
  referral: Referral;
  form: EpikriseForm;
  update: (patch: Partial<EpikriseForm>) => void;
  attempted: boolean;
  issues: EpikriseIssue[];
  /** XDIS05 (DIS05-S 4.22): the logged-in user's name and title. */
  signer: { name: string | null; title: string | null; loading: boolean };
}) {
  const { t } = useTranslation();
  const referralRelative = Boolean(referral.parent_1_name?.trim());
  const relativeCount = form.relatives.length + (form.includeReferralRelative && referralRelative ? 1 : 0);

  const dateLabels: Record<EpikriseKind, [string, string]> = {
    XDIS01: [t('referrals.epikrise_admission', 'Indlæggelse'), t('referrals.epikrise_discharge', 'Udskrivning')],
    XDIS02: [t('referrals.epikrise_first_visit', 'Første ambulante besøg'), t('referrals.epikrise_last_visit', 'Sidste ambulante besøg')],
    XDIS03: [
      t('referrals.epikrise_casualty_first', 'Første skadestuebesøg'),
      t('referrals.epikrise_casualty_last', 'Sidste skadestuebesøg'),
    ],
    XDIS05: [t('referrals.epikrise_exam_start', 'Undersøgelse start'), t('referrals.epikrise_exam_end', 'Undersøgelse slut')],
  };
  const [startLabel, endLabel] = dateLabels[form.kind];

  return (
    <>
      <div className={SUB}>
        <div className={HEADING}>{t('referrals.epikrise_diagnoses', 'Diagnoser (SKS)')}</div>
        <DiagnosisField
          inputId="epikrise-main-diagnosis"
          label={
            epikriseRequiresMainDiagnosis(form.kind)
              ? t('referrals.epikrise_main_diagnosis', 'Aktionsdiagnose (præcis én)')
              : t('referrals.epikrise_main_diagnosis_optional', 'Aktionsdiagnose (højst én, valgfri)')
          }
          selected={form.mainDiagnosis ? { code: form.mainDiagnosis.code, textDa: form.mainDiagnosis.text } : null}
          onSelect={(d) => update({ mainDiagnosis: { code: d.code, text: diagnoseTextForReferral(d.textDa) } })}
          onClear={() => update({ mainDiagnosis: null })}
        />
        {attempted && issues.includes('main_diagnosis') && (
          <p role="alert" className={FORM_ERROR_TYPE}>
            {t('referrals.epikrise_main_diagnosis_missing', 'Vælg én aktionsdiagnose.')}
          </p>
        )}
        <div className={FORM_LABEL_TYPE}>
          {t('referrals.epikrise_additional', 'Aktionstillægsdiagnoser ({{count}} af højst {{max}})', {
            count: form.mainAdditional.length,
            max: EPIKRISE_MAIN_ADDITIONAL_MAX,
          })}
        </div>
        <ul className="space-y-1" data-testid="epikrise-additional-list">
          {form.mainAdditional.map((d, i) => (
            <li key={`${d.code}-${i}`} className="flex items-center justify-between gap-2 text-sm text-ds-text">
              <span>
                <span className="font-mono">{d.code}</span> {d.text}
              </span>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => update({ mainAdditional: form.mainAdditional.filter((_, j) => j !== i) })}
              >
                {t('referrals.epikrise_remove', 'Fjern')}
              </Button>
            </li>
          ))}
        </ul>
        {form.mainAdditional.length < EPIKRISE_MAIN_ADDITIONAL_MAX ? (
          <DiagnosisField
            key={`additional-${form.mainAdditional.length}`}
            inputId="epikrise-additional-diagnosis"
            label={t('referrals.epikrise_additional_add', 'Tilføj aktionstillægsdiagnose')}
            selected={null}
            onSelect={(d) =>
              update({ mainAdditional: [...form.mainAdditional, { code: d.code, text: diagnoseTextForReferral(d.textDa) }] })
            }
            onClear={() => undefined}
          />
        ) : (
          <p className={FORM_HINT_TYPE} data-testid="epikrise-additional-max">
            {t('referrals.epikrise_additional_max', 'Der kan højst vælges {{max}} aktionstillægsdiagnoser.', {
              max: EPIKRISE_MAIN_ADDITIONAL_MAX,
            })}
          </p>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {(['start', 'end'] as const).map((which) => {
          const label = which === 'start' ? startLabel : endLabel;
          return (
            <div key={which}>
              <span className={cn('block', FORM_LABEL_TYPE)}>
                {label} <span className="font-mono text-xs text-ds-muted">({EPIKRISE_DATE_ELEMENTS[form.kind][which]})</span>
              </span>
              <div className="flex flex-wrap items-start gap-2">
                <div className="min-w-[11rem] flex-1" data-testid={`epikrise-${which}-date`}>
                  <DanishDateField
                    name={`epikrise-${which}-date`}
                    value={form[which].date}
                    calendar
                    onChange={(date) => update({ [which]: { ...form[which], date } })}
                  />
                </div>
                <TimeInput
                  name={`epikrise-${which}-time`}
                  label={`${label}, ${t('referrals.epikrise_time', 'tidspunkt')}`}
                  value={form[which].time}
                  allowEmpty
                  onChange={(time) => update({ [which]: { ...form[which], time } })}
                />
              </div>
            </div>
          );
        })}
        {attempted && issues.includes('end_before_start') && (
          <p role="alert" className={cn('sm:col-span-2', FORM_ERROR_TYPE)}>
            {t('referrals.epikrise_end_before_start', 'Slutdatoen ligger før startdatoen.')}
          </p>
        )}
      </div>

      {epikriseHasRadiologyExtras(form.kind) ? (
        <div className={SUB} data-testid="epikrise-signer">
          <div className={HEADING}>{t('referrals.epikrise_signer', 'Underskriver (fra login)')}</div>
          {signer.loading ? (
            <p className={FORM_HINT_TYPE}>{t('referrals.epikrise_signer_loading', 'Henter dit navn og din titel …')}</p>
          ) : (
            <>
              <p className="text-sm text-ds-text" data-testid="epikrise-signature">
                {epikriseSignature({ title: signer.title || form.contact.title, name: signer.name }) || '—'}
                <span className={cn('ml-2', FORM_HINT_TYPE)}>
                  {t('referrals.epikrise_signer_date', 'Dato og klokkeslæt sættes automatisk, når epikrisen dannes.')}
                </span>
              </p>
              {!signer.title && (
                <div>
                  <label htmlFor="epikrise-contact-title" className={cn('block', FORM_LABEL_TYPE)}>
                    {t('referrals.epikrise_signer_title', 'Titel (din bruger har ingen titel i klinikken)')}
                  </label>
                  <input
                    id="epikrise-contact-title"
                    type="text"
                    maxLength={EPIKRISE_CONTACT_MAX}
                    value={form.contact.title}
                    onChange={(e) => update({ contact: { ...form.contact, title: e.target.value } })}
                    className={cn(TYPE.inputValue, FIELD)}
                  />
                </div>
              )}
              <p className={FORM_HINT_TYPE}>
                {t(
                  'referrals.epikrise_signer_hint',
                  'Navn og titel hentes fra den bruger, der er logget ind, og sendes som kontaktperson (Contact: titel og navn); tidspunktet sendes som ClinicalInformation/Signed.',
                )}
              </p>
              {attempted && issues.includes('signer_missing') && (
                <p role="alert" className={FORM_ERROR_TYPE}>
                  {t('referrals.epikrise_signer_missing', 'Din bruger har intet navn. Angiv dit navn i din profil, før epikrisen dannes.')}
                </p>
              )}
              {attempted && issues.includes('contact_too_long') && (
                <p role="alert" className={FORM_ERROR_TYPE}>
                  {t('referrals.epikrise_contact_too_long', 'Kontaktpersonens titel og navn må højst være {{max}} tegn hver.', {
                    max: EPIKRISE_CONTACT_MAX,
                  })}
                </p>
              )}
            </>
          )}
        </div>
      ) : (
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="epikrise-contact-title" className={cn('block', FORM_LABEL_TYPE)}>
            {t('referrals.reply_contact_title', 'Kontaktperson, titel')}
          </label>
          <input
            id="epikrise-contact-title"
            type="text"
            maxLength={EPIKRISE_CONTACT_MAX}
            value={form.contact.title}
            onChange={(e) => update({ contact: { ...form.contact, title: e.target.value } })}
            className={cn(TYPE.inputValue, FIELD)}
          />
        </div>
        <div>
          <label htmlFor="epikrise-contact-name" className={cn('block', FORM_LABEL_TYPE)}>
            {t('referrals.epikrise_contact_name_optional', 'Kontaktperson, navn (valgfrit)')}
          </label>
          <input
            id="epikrise-contact-name"
            type="text"
            maxLength={EPIKRISE_CONTACT_MAX}
            value={form.contact.name}
            onChange={(e) => update({ contact: { ...form.contact, name: e.target.value } })}
            aria-invalid={attempted && issues.includes('contact_too_long')}
            className={cn(TYPE.inputValue, FIELD)}
          />
        </div>
        {attempted && issues.includes('contact_too_long') && (
          <p className={FORM_ERROR_TYPE}>
            {t('referrals.epikrise_contact_too_long', 'Kontaktpersonens titel og navn må højst være {{max}} tegn hver.', {
              max: EPIKRISE_CONTACT_MAX,
            })}
          </p>
        )}
      </div>
      )}

      <div className={SUB}>
        <div className={HEADING}>
          {t('referrals.epikrise_relatives', 'Pårørende ({{count}} af højst {{max}})', {
            count: relativeCount,
            max: EPIKRISE_RELATIVES_MAX,
          })}
        </div>
        {referralRelative && (
          <label className="flex items-center gap-2 text-sm text-ds-text">
            <input
              type="checkbox"
              checked={form.includeReferralRelative}
              onChange={(e) => update({ includeReferralRelative: e.target.checked })}
            />
            {t('referrals.epikrise_referral_relative', 'Pårørende fra henvisningen: {{name}}', { name: referral.parent_1_name })}
          </label>
        )}
        {form.relatives.map((r, i) => (
          <div key={i} className="grid gap-2 sm:grid-cols-4">
            <select
              aria-label={t('referrals.epikrise_relation', 'Relation')}
              value={r.relationCode}
              onChange={(e) =>
                update({
                  relatives: form.relatives.map((x, j) =>
                    j === i ? { ...x, relationCode: e.target.value as typeof r.relationCode } : x,
                  ),
                })
              }
              className={cn(TYPE.inputValue, FIELD)}
            >
              <option value="uspec_paaroerende">{t('referrals.epikrise_relation_uspec', 'Pårørende (fx ægtefælle)')}</option>
              <option value="mor">{t('referrals.epikrise_relation_mor', 'Mor')}</option>
              <option value="far">{t('referrals.epikrise_relation_far', 'Far')}</option>
              <option value="barn">{t('referrals.epikrise_relation_barn', 'Barn')}</option>
            </select>
            <input
              aria-label={t('referrals.epikrise_relative_given', 'Fornavn')}
              placeholder={t('referrals.epikrise_relative_given', 'Fornavn')}
              value={r.givenName}
              onChange={(e) => update({ relatives: form.relatives.map((x, j) => (j === i ? { ...x, givenName: e.target.value } : x)) })}
              className={cn(TYPE.inputValue, FIELD)}
            />
            <input
              aria-label={t('referrals.epikrise_relative_surname', 'Efternavn')}
              placeholder={t('referrals.epikrise_relative_surname', 'Efternavn')}
              value={r.surname}
              onChange={(e) => update({ relatives: form.relatives.map((x, j) => (j === i ? { ...x, surname: e.target.value } : x)) })}
              className={cn(TYPE.inputValue, FIELD)}
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => update({ relatives: form.relatives.filter((_, j) => j !== i) })}
            >
              {t('referrals.epikrise_remove', 'Fjern')}
            </Button>
          </div>
        ))}
        {relativeCount < EPIKRISE_RELATIVES_MAX && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => update({ relatives: [...form.relatives, { relationCode: 'uspec_paaroerende', surname: '', givenName: '' }] })}
          >
            {t('referrals.epikrise_relative_add', 'Tilføj pårørende')}
          </Button>
        )}
        {attempted && issues.includes('relative_surname') && (
          <p role="alert" className={FORM_ERROR_TYPE}>
            {t('referrals.epikrise_relative_surname_missing', 'Angiv efternavn på pårørende.')}
          </p>
        )}
      </div>
    </>
  );
}
